<template>
  <div>
    <Nav></Nav>
    <b-overlay :show="loadingReports" rounded="sm" spinner-type="grow">
      <b-container fluid>
        <b-row>
          <b-col>
            <h1>Reporte de Desempeño de Meseros</h1>
            <p class="text-muted">Análisis detallado del rendimiento de cada mesero por día</p>
          </b-col>
        </b-row>

        <WaiterReportSearchBar @search="searchRecv" @changed="filtersChanged"></WaiterReportSearchBar>

        <!-- Message when filters have changed -->
        <b-card v-if="filtersHaveChanged" class="mt-5">
          <b-row>
            <b-col class="text-center py-5">
              <b-icon icon="search" font-scale="3" variant="primary"></b-icon>
              <h4 class="mt-3">Filtros modificados</h4>
              <p class="text-muted">Presiona el botón "Buscar" para actualizar el reporte con los nuevos criterios</p>
            </b-col>
          </b-row>
        </b-card>

        <!-- Report content -->
        <b-card class="mt-5" v-else-if="reportTable.length > 0 && !filtersHaveChanged">
          <!-- Summary Cards -->
          <b-row class="mb-4">
            <b-col md="3" sm="6" class="mb-3">
              <b-card bg-variant="primary" text-variant="white">
                <h4>{{ totalOperations }}</h4>
                <small>Total de Operaciones</small>
              </b-card>
            </b-col>
            <b-col md="3" sm="6" class="mb-3">
              <b-card bg-variant="success" text-variant="white">
                <h4>${{ totalSales.toFixed(2) }}</h4>
                <small>Ventas Totales</small>
              </b-card>
            </b-col>
            <b-col md="3" sm="6" class="mb-3">
              <b-card bg-variant="info" text-variant="white">
                <h4>{{ totalPersons }}</h4>
                <small>Total de Comensales</small>
              </b-card>
            </b-col>
            <b-col md="3" sm="6" class="mb-3">
              <b-card bg-variant="warning" text-variant="white">
                <h4>${{ totalTips.toFixed(2) }}</h4>
                <small>Propinas Totales</small>
              </b-card>
            </b-col>
          </b-row>

          <!-- Export Button -->
          <b-row>
            <b-col>
              <b-button variant="primary" @click="exportToExcel">
                <b-icon icon="download"></b-icon> Descargar Excel
              </b-button>
            </b-col>
          </b-row>

          <!-- Data Table -->
          <b-row class="mt-3">
            <b-col>
              <b-table
                small
                striped
                hover
                :items="reportTable"
                :fields="tableFields"
                responsive
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
              >
                <template #table-busy>
                  <div class="text-center text-danger my-2">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong>Cargando...</strong>
                  </div>
                </template>

                <!-- Format currency fields -->
                <template #cell(total_ventas)="data">
                  ${{ data.value.toFixed(2) }}
                </template>
                <template #cell(promedio_ticket)="data">
                  ${{ data.value.toFixed(2) }}
                </template>
                <template #cell(promedio_venta_comensal)="data">
                  ${{ data.value.toFixed(2) }}
                </template>
                <template #cell(total_propinas)="data">
                  ${{ data.value.toFixed(2) }}
                </template>
                <template #cell(promedio_propina)="data">
                  ${{ data.value.toFixed(2) }}
                </template>

                <!-- Format percentage fields -->
                <template #cell(porcentaje_propina)="data">
                  {{ data.value.toFixed(1) }}%
                </template>
                <template #cell(tasa_cancelacion)="data">
                  <span :class="data.value > 10 ? 'text-danger font-weight-bold' : ''">
                    {{ data.value.toFixed(1) }}%
                  </span>
                </template>

                <!-- Format decimal fields -->
                <template #cell(promedio_comensales_ticket)="data">
                  {{ data.value.toFixed(1) }}
                </template>
                <template #cell(tiempo_promedio_atencion)="data">
                  {{ data.value.toFixed(0) }} min
                </template>

              </b-table>
            </b-col>
          </b-row>
        </b-card>

        <!-- No data message -->
        <b-card v-else-if="!loadingReports && !filtersHaveChanged" class="mt-5">
          <b-row>
            <b-col class="text-center py-5">
              <b-icon icon="info-circle" font-scale="3" variant="secondary"></b-icon>
              <h4 class="mt-3">No hay datos disponibles</h4>
              <p class="text-muted">Selecciona un rango de fechas y sucursales para ver el reporte</p>
            </b-col>
          </b-row>
        </b-card>
      </b-container>
    </b-overlay>
  </div>
</template>

<script>
import Nav from "../../components/Nav.vue";
import axios from "axios";
import moment from "moment";
import WaiterReportSearchBar from "./searchBar/WaiterReportSearchBar.vue";
import * as XLSX from "xlsx";

export default {
  name: "WaiterPerformance",
  components: { Nav, WaiterReportSearchBar },
  data() {
    return {
      loadingReports: false,
      reportTable: [],
      filtersHaveChanged: false,
      dates: {
        initial_date: "",
        end_date: "",
        sucursales: [],
        waiters: []
      },
      sortBy: 'total_ventas',
      sortDesc: true,
      tableFields: [
        { key: "date", label: "Fecha", sortable: true },
        { key: "waiter_name", label: "Mesero", sortable: true, stickyColumn: true },
        { key: "sucursal_name", label: "Sucursal", sortable: true },
        { key: "restaurant_area_name", label: "Área", sortable: true },
        { key: "numero_operaciones", label: "Operaciones", sortable: true },
        { key: "total_comensales", label: "Comensales", sortable: true },
        { key: "total_ventas", label: "Ventas", sortable: true },
        { key: "promedio_ticket", label: "Ticket Prom.", sortable: true },
        { key: "promedio_comensales_ticket", label: "Comensales/Ticket", sortable: true },
        { key: "promedio_venta_comensal", label: "Venta/Comensal", sortable: true },
        { key: "total_propinas", label: "Propinas", sortable: true },
        { key: "promedio_propina", label: "Propina Prom.", sortable: true },
        { key: "porcentaje_propina", label: "% Propina", sortable: true },
        { key: "tickets_cancelados", label: "Cancelados", sortable: true },
        { key: "tasa_cancelacion", label: "% Cancelación", sortable: true },
        { key: "tiempo_promedio_atencion", label: "Tiempo Prom.", sortable: true },
      ],
    };
  },
  computed: {
    totalOperations() {
      return this.reportTable.reduce((sum, item) => sum + item.numero_operaciones, 0);
    },
    totalSales() {
      return this.reportTable.reduce((sum, item) => sum + item.total_ventas, 0);
    },
    totalPersons() {
      return this.reportTable.reduce((sum, item) => sum + item.total_comensales, 0);
    },
    totalTips() {
      return this.reportTable.reduce((sum, item) => sum + item.total_propinas, 0);
    }
  },
  methods: {
    exportToExcel() {
      const workbook = XLSX.utils.book_new();

      // Prepare headers
      const headers = this.tableFields.map(field => field.label);

      // Prepare data rows with proper number types
      const data = [headers];
      this.reportTable.forEach(item => {
        const row = [];
        this.tableFields.forEach(field => {
          let value = item[field.key];

          // Keep numbers as numbers (not strings) for Excel
          if (["numero_operaciones", "total_comensales", "tickets_cancelados",
               "waiter_id", "sucursal_id", "restaurant_area_id"].includes(field.key)) {
            // Integer fields - keep as number
            value = typeof value === 'number' ? value : 0;
          } else if (["total_ventas", "promedio_ticket", "promedio_venta_comensal",
                      "total_propinas", "promedio_propina", "promedio_comensales_ticket",
                      "porcentaje_propina", "tasa_cancelacion", "tiempo_promedio_atencion"].includes(field.key)) {
            // Decimal/float fields - keep as number
            value = typeof value === 'number' ? value : 0;
          } else {
            // Text fields (date, names)
            value = value || "";
          }

          row.push(value);
        });
        data.push(row);
      });

      // Create worksheet from array of arrays
      const worksheet = XLSX.utils.aoa_to_sheet(data);

      // Apply number formatting to specific columns
      const range = XLSX.utils.decode_range(worksheet['!ref']);

      for (let R = 1; R <= range.e.r; ++R) { // Start from row 1 (skip header)
        this.tableFields.forEach((field, C) => {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          if (!worksheet[cellAddress]) return;

          // Apply formatting based on field type
          if (["total_ventas", "promedio_ticket", "promedio_venta_comensal",
               "total_propinas", "promedio_propina"].includes(field.key)) {
            // Currency format with 2 decimals
            worksheet[cellAddress].z = '$#,##0.00';
          } else if (["porcentaje_propina", "tasa_cancelacion"].includes(field.key)) {
            // Percentage format with 1 decimal
            worksheet[cellAddress].z = '0.0%';
            // Convert from 15.5 to 0.155 for percentage display
            if (typeof worksheet[cellAddress].v === 'number') {
              worksheet[cellAddress].v = worksheet[cellAddress].v / 100;
            }
          } else if (field.key === "promedio_comensales_ticket") {
            // Number with 1 decimal
            worksheet[cellAddress].z = '0.0';
          } else if (field.key === "tiempo_promedio_atencion") {
            // Integer (minutes)
            worksheet[cellAddress].z = '0 "min"';
          } else if (["numero_operaciones", "total_comensales", "tickets_cancelados"].includes(field.key)) {
            // Integer format
            worksheet[cellAddress].z = '#,##0';
          }
        });
      }

      // Set column widths
      const colWidths = this.tableFields.map(field => {
        if (["date", "waiter_name", "sucursal_name", "restaurant_area_name"].includes(field.key)) {
          return { wch: 15 };
        } else if (["total_ventas", "promedio_ticket"].includes(field.key)) {
          return { wch: 12 };
        }
        return { wch: 10 };
      });
      worksheet['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Desempeño Meseros");

      // Generate filename
      const fileName = `desempeno_meseros_${this.dates.initial_date}_a_${this.dates.end_date}.xlsx`;

      // Download
      XLSX.writeFile(workbook, fileName);
    },
    filtersChanged(p) {
      console.log("Filters changed", p);
      this.filtersHaveChanged = true;
    },
    searchRecv(p) {
      console.log("searchRecv", p);
      this.dates.end_date = moment(p.endDate).format("YYYY-MM-DD");
      this.dates.initial_date = moment(p.initDate).format("YYYY-MM-DD");
      this.dates.sucursales = p.sucursales;
      this.dates.waiters = p.waiters || [];
      this.filtersHaveChanged = false;
      this.getReport();
    },
    getReport() {
      console.log("Getting waiter performance report");
      this.loadingReports = true;
      let that = this;
      this.reportTable = [];

      axios
        .post("/report/dates/waiter", this.dates)
        .then(function (response) {
          console.log("Report received:", response.data);
          that.reportTable = response.data;
        })
        .catch(function (err) {
          console.error("Error fetching report:", err);
          that.makeToast("Error", err.response?.data || "Error al cargar el reporte", "danger");
        })
        .finally(() => {
          that.loadingReports = false;
        });
    },
    makeToast(title, content, variant) {
      this.$bvToast.toast(content, {
        title,
        variant,
        autoHideDelay: 5000,
        appendToast: true,
      });
    },
  },
};
</script>

<style scoped>
.b-table th {
  white-space: nowrap;
}
</style>
